// RouterMetricsCard.jsx — Router activity widget.
// Renders the 24h tiles + last 5 request-ids for the gs-router.
// Works for BOTH master admin (pass `endpoint="/admin/gymsales/router-metrics"`)
// and tenant supervisor/admin (pass `endpoint="/tenant/gymsales/router-metrics"`).
//
// Language is deliberately plain — this widget will be read by supervisors
// who don't know what a 429 or an x-request-id is. Tooltips explain the
// nerdy bits without dumping jargon into the primary labels.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Activity, AlertCircle, Copy, CheckCircle2, XCircle, RefreshCw, Loader2, Timer, Zap, TrendingUp,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Tile = ({ label, value, hint, tone = 'neutral', testid }) => {
  const toneClasses = {
    neutral: 'text-slate-900',
    good: 'text-emerald-600',
    warn: 'text-amber-700',
    bad: 'text-rose-600',
  }[tone] || 'text-slate-900';
  return (
    <div className="bg-white shadow-sm border border-slate-200 rounded-lg p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <div className={`text-2xl font-bold tabular-nums ${toneClasses}`}>{value}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
};

const fmtRel = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
};

const RequestIdRow = ({ row }) => {
  const [copied, setCopied] = useState(false);
  const rid = row.request_id;
  const copy = useCallback(() => {
    if (!rid) return;
    navigator.clipboard.writeText(rid).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [rid]);
  const isErr = row.status_code >= 400 || !row.status_code;
  return (
    <tr className="border-t border-slate-100">
      <td className="px-3 py-2 text-slate-600 whitespace-nowrap text-[11px]">{fmtRel(row.created_at)}</td>
      <td className="px-3 py-2 font-mono text-[10px] text-slate-700 truncate max-w-[220px]">{row.path}</td>
      <td className="px-3 py-2 text-center">
        {isErr
          ? <span className="inline-flex items-center gap-1 text-rose-600 text-xs" title={row.error_code || row.error || 'error'}>
              <XCircle className="w-3.5 h-3.5" />{row.status_code || 'ERR'}
            </span>
          : <span className="inline-flex items-center gap-1 text-emerald-600 text-xs">
              <CheckCircle2 className="w-3.5 h-3.5" />{row.status_code}
            </span>}
      </td>
      <td className="px-3 py-2 text-right text-slate-600 tabular-nums text-[11px]">{Math.round(row.duration_ms || 0)}ms</td>
      <td className="px-3 py-2">
        <button
          type="button"
          onClick={copy}
          disabled={!rid}
          title={rid ? `Copy ${rid}` : 'No request id — usually a local network error'}
          className="inline-flex items-center gap-1 text-[10px] text-slate-600 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 disabled:opacity-30 border border-slate-200 rounded px-2 py-1 font-mono"
          data-testid={`copy-request-id-${rid || 'none'}`}
        >
          {copied ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
          {rid ? `${rid.slice(0, 8)}…` : '—'}
        </button>
      </td>
    </tr>
  );
};

/**
 * @param {object} props
 * @param {string} props.endpoint  Backend path (already includes `/admin/...` or `/tenant/...`)
 * @param {string} [props.title]   Optional title override
 */
const RouterMetricsCard = ({ endpoint = '/tenant/gymsales/router-metrics', title = 'Router activity — last 24h' }) => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    setErr('');
    try {
      const r = await axios.get(`${API}${endpoint}?hours=24`, { headers });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load router activity.');
    } finally { setLoading(false); }
  }, [headers, endpoint]);

  useEffect(() => { load(); const t = setInterval(() => load({ silent: true }), 30_000); return () => clearInterval(t); }, [load]);

  if (loading && !data) {
    return (
      <div className="bg-white shadow-sm border border-slate-200 rounded-xl p-4 flex items-center justify-center min-h-[100px]" data-testid="router-metrics-loading">
        <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
      </div>
    );
  }
  if (err && !data) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-rose-800 text-xs flex items-start gap-2" data-testid="router-metrics-error">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />{err}
      </div>
    );
  }
  if (!data) return null;

  const t = data.tiles || {};
  return (
    <div className="bg-white shadow-sm border border-slate-200 rounded-xl overflow-hidden" data-testid="router-metrics-card">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 text-slate-900 font-semibold text-sm">
        <Activity className="w-4 h-4 text-emerald-600" />
        {title}
        <button
          type="button"
          onClick={() => load()}
          className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-900"
          data-testid="router-metrics-refresh"
        >
          <RefreshCw className="w-3 h-3" />Refresh
        </button>
      </div>

      <div className="p-3">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
          <Tile
            label="Calls"
            value={t.total_calls || 0}
            hint="Total requests"
            testid="tile-total-calls"
          />
          <Tile
            label="Slow calls"
            value={`${Math.round(t.avg_latency_ms || 0)}ms`}
            hint="Average response time"
            tone={(t.avg_latency_ms || 0) > 2000 ? 'warn' : 'good'}
            testid="tile-avg-latency"
          />
          <Tile
            label="Waits"
            value={t.rate_limited_429 || 0}
            hint="Times we hit the speed limit"
            tone={(t.rate_limited_429 || 0) > 0 ? 'warn' : 'neutral'}
            testid="tile-rate-limited"
          />
          <Tile
            label="Retries that worked"
            value={t.retry_wins || 0}
            hint="Recovered after waiting"
            tone="good"
            testid="tile-retry-wins"
          />
          <Tile
            label="Errors"
            value={(t.errors_4xx || 0) + (t.errors_5xx || 0)}
            hint={`${t.errors_4xx || 0} client · ${t.errors_5xx || 0} server`}
            tone={((t.errors_4xx || 0) + (t.errors_5xx || 0)) > 0 ? 'bad' : 'neutral'}
            testid="tile-errors"
          />
        </div>

        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Recent request IDs · quote these in a support ticket
            </div>
          </div>
          {(!data.recent_request_ids || data.recent_request_ids.length === 0) ? (
            <div className="text-slate-400 text-xs italic px-3 py-4 border border-slate-100 rounded">
              No requests with a router ID yet. Once GymSales lookups start flowing, the last 5 will appear here.
            </div>
          ) : (
            <div className="border border-slate-100 rounded overflow-hidden">
              <table className="w-full text-xs" data-testid="recent-request-ids-table">
                <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-semibold">When</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Endpoint</th>
                    <th className="text-center px-3 py-1.5 font-semibold">Status</th>
                    <th className="text-right px-3 py-1.5 font-semibold">ms</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Request ID</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_request_ids.map((row, i) => (
                    <RequestIdRow key={row.request_id + i} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RouterMetricsCard;
